/**
 * Replaces the visible product media gallery with the pre-rendered gallery
 * belonging to the selected Device Colour Gallery metaobject.
 *
 * The templates are rendered by product-media-gallery-content.liquid, so the
 * replacement keeps the theme's native image, video, thumbnail, zoom, swipe,
 * and responsive behaviour.
 *
 * @param {HTMLButtonElement} button
 */
function selectDeviceColourGallery(button) {
  const mediaRegion = button.closest('.product-information__media');
  const galleryHandle = button.dataset.galleryHandle;

  if (!mediaRegion || !galleryHandle) return;

  const galleryTemplates = mediaRegion.querySelectorAll('template[data-device-gallery-template]');
  const galleryTemplate = Array.from(galleryTemplates).find(
    (template) => template.dataset.deviceGalleryTemplate === galleryHandle
  );
  const currentGallery = mediaRegion.querySelector(':scope > media-gallery');
  const nextGallery = galleryTemplate?.content.querySelector('media-gallery')?.cloneNode(true);

  if (!currentGallery || !(nextGallery instanceof HTMLElement)) return;

  currentGallery.querySelectorAll('video').forEach((video) => video.pause());
  currentGallery.replaceWith(nextGallery);

  const firstSlide = nextGallery.querySelector('slideshow-slide');
  if (firstSlide instanceof HTMLElement) {
    requestAnimationFrame(() => syncProductGalleryVideoPlayback(nextGallery, firstSlide));
  }

  const selector = button.closest('device-color');
  selector?.querySelectorAll('.device-color__item').forEach((item) => {
    const isActive = item === button;
    item.classList.toggle('active', isActive);
    item.setAttribute('aria-pressed', String(isActive));
  });
}

/**
 * Keeps product-gallery videos aligned with the active slideshow slide.
 * Explicit play() is needed on iOS Safari because an autoplay video that was
 * initially off-screen can remain paused after a touch swipe.
 *
 * @param {HTMLElement} gallery
 * @param {HTMLElement} activeSlide
 */
function syncProductGalleryVideoPlayback(gallery, activeSlide) {
  gallery.querySelectorAll('slideshow-slide').forEach((slide) => {
    const isActive = slide === activeSlide;
    const video = slide.querySelector('video');
    const deferredMedia = slide.querySelector('deferred-media');

    if (video instanceof HTMLVideoElement) {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');

      if (isActive) {
        const playPromise = video.play();
        if (playPromise !== undefined) playPromise.catch(() => {});
      } else {
        video.pause();
      }
    } else if (deferredMedia instanceof HTMLElement) {
      const playbackMethod = isActive ? 'playMedia' : 'pauseMedia';
      if (typeof deferredMedia[playbackMethod] === 'function') {
        deferredMedia[playbackMethod]();
      }
    }
  });
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const button = event.target.closest('.device-color__item');
  if (!(button instanceof HTMLButtonElement)) return;

  selectDeviceColourGallery(button);
});

document.addEventListener('slideshow:select', (event) => {
  if (!(event.target instanceof Element)) return;

  const gallery = event.target.closest('media-gallery');
  const activeSlide = event.detail?.slide;

  if (!(gallery instanceof HTMLElement) || !(activeSlide instanceof HTMLElement)) return;
  if (!gallery.closest('.product-information__media')) return;

  syncProductGalleryVideoPlayback(gallery, activeSlide);
});

/**
 * The theme replaces media-gallery automatically after a model variant change.
 * Keep the adjacent colour selector and gallery templates in sync with the same
 * section-rendering response.
 */
document.addEventListener('variant:update', (event) => {
  const source = event.detail?.data?.html;
  const section = event.target instanceof Element ? event.target.closest('.shopify-section') : null;

  if (!(source instanceof Document) || !section) return;

  const currentSelector = section.querySelector('.device-color__wrapper');
  const nextSelector = source.querySelector('.device-color__wrapper');
  const currentTemplates = section.querySelector('.device-colour-gallery-templates');
  const nextTemplates = source.querySelector('.device-colour-gallery-templates');
  const currentMediaRegion = section.querySelector('.product-information__media');
  const currentWrapper = section.querySelector('.product_info_wrp');
  const nextWrapper = source.querySelector('.product_info_wrp');

  if (currentSelector && nextSelector) currentSelector.replaceWith(nextSelector);
  if (currentTemplates && nextTemplates) currentTemplates.replaceWith(nextTemplates);
  else if (!currentTemplates && nextTemplates && currentMediaRegion) currentMediaRegion.append(nextTemplates);
  else if (currentTemplates && !nextTemplates) currentTemplates.remove();

  if (currentWrapper && nextWrapper) {
    currentWrapper.dataset.deviceGalleryEnabled = nextWrapper.dataset.deviceGalleryEnabled || 'false';
  }
});


// cart upsell slider
// 
function initUpsellCustomSlider() {

  const slider = document.querySelector('.upsell-slider');
  if (!slider) return;

  const track = slider.querySelector('.upsell-track');
  const slides = slider.querySelectorAll('.upsell-card');
  const dotsWrap = slider.querySelector('.upsell-dots');

  if (!track || slides.length === 0 || !dotsWrap) return;

  let index = 0;
  let startX = 0;
  let isDragging = false;

  // 🔥 RESET STATE (important on re-init)
  track.style.transform = 'translateX(0)';
  dotsWrap.innerHTML = '';

  // ✅ 👉 SINGLE SLIDE FIX
  if (slides.length <= 1) {
    dotsWrap.style.display = 'none';
    track.style.transform = 'none';

    // ❌ remove drag events
    track.onmousedown = null;
    track.onmouseup = null;
    track.onmouseleave = null;
    track.ontouchstart = null;
    track.ontouchend = null;

    return;
  }

  // ✅ show dots if multiple
  dotsWrap.style.display = 'flex';

  // 🔥 CREATE DOTS
  slides.forEach((_, i) => {
    const dot = document.createElement('button');
    dot.className = 'upsell-dot';
    if (i === 0) dot.classList.add('active');

    dot.addEventListener('click', () => {
      index = i;
      updateSlider();
    });

    dotsWrap.appendChild(dot);
  });

  function updateSlider() {
    track.style.transform = `translateX(-${index * 100}%)`;

    dotsWrap.querySelectorAll('.upsell-dot').forEach((d, i) => {
      d.classList.toggle('active', i === index);
    });
  }

  // 🔥 DRAG (clean override every init)
  track.onmousedown = (e) => {
    isDragging = true;
    startX = e.pageX;
  };

  track.onmouseup = (e) => {
    if (!isDragging) return;

    let diff = e.pageX - startX;

    if (diff > 50 && index > 0) index--;
    if (diff < -50 && index < slides.length - 1) index++;

    updateSlider();
    isDragging = false;
  };

  track.onmouseleave = () => isDragging = false;

  // 🔥 TOUCH
  track.ontouchstart = (e) => {
    startX = e.touches[0].clientX;
  };

  track.ontouchend = (e) => {
    let diff = e.changedTouches[0].clientX - startX;

    if (diff > 50 && index > 0) index--;
    if (diff < -50 && index < slides.length - 1) index++;

    updateSlider();
  };
}




// 🔥 First load (fallback)
document.addEventListener('DOMContentLoaded', function () {
  setTimeout(initUpsellCustomSlider, 500);
});

// 🔥 Shopify cart drawer update
document.addEventListener('cart:update', function () {
  setTimeout(initUpsellCustomSlider, 300);
});

// 🔥 EXTRA SAFETY (when section re-renders silently)
document.addEventListener('shopify:section:load', function () {
  setTimeout(initUpsellCustomSlider, 300);
});
/* VARIANT CHANGE */
document.addEventListener('change', function(e){
  if(e.target.classList.contains('upsell-variant-select')){
    const select = e.target;
    const card = select.closest('.upsell-card');
    const price = select.selectedOptions[0].dataset.price;
    card.querySelector('.upsell-price').innerText = price;
  }
});

/* ADD TO CART */
document.addEventListener('click', function(e){

  const btn = e.target.closest('.upsell-btn');
  if(!btn) return;

  const card = btn.closest('.upsell-card');
  const variantId = card.querySelector('.upsell-variant-select').value;

  btn.disabled = true;
  btn.innerText = 'Adding...';

  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      id: variantId,
      quantity: 1
    })
  })
  .then(res => res.json())
  .then(() => {
    btn.innerText = '✓ Added';
    refreshCartDrawer();
  })
  .catch(() => {
    btn.disabled = false;
    btn.innerText = 'Add';
  });

});



function refreshCartDrawer() {
  fetch('/?sections=cart-drawer')
    .then(res => res.json())
    .then(data => {
      document.dispatchEvent(new CustomEvent('cart:update', {
        detail: { data: { sections: data } }
      }));
      initUpsellCustomSlider();
    });
}
